import { useMemo, useState } from "react";

import {
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { NetworkSearchBar } from "components/Network/NetworkSearchBar";
import { NetworkSortMenu } from "components/Network/NetworkSortMenu";
import { filterAndSortNetworkItems } from "utils/network";

import { EstablishedNetworkTableRow } from "./EstablishedNetworkTableRow";

import type { NetworkSortType } from "components/Network/NetworkSortMenu";

interface Props {
  error: string | null;
  loading: boolean;
  followed: FollowedShipper[];
  onUnfollowShipperClicked: (shipper: FollowedShipper) => void;
}

export function EstablishedNetwork(props: Props) {
  const { error, loading, followed, onUnfollowShipperClicked } = props;

  const [sortType, setSortType] = useState<NetworkSortType>("CONTACT_NAME");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const filteredAndSortedFollowed = useMemo(
    () => filterAndSortNetworkItems(followed, sortType, searchQuery),
    [followed, sortType, searchQuery],
  );

  if (loading) {
    return (
      <Stack alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }
  if (error) {
    return (
      <Stack>
        <Typography color="red">{error}</Typography>
      </Stack>
    );
  }
  if (followed.length === 0) {
    return (
      <Stack>
        <Typography variant="h6">
          You are not currently following anyone.
        </Typography>
      </Stack>
    );
  }
  return (
    <Stack spacing={2}>
      <NetworkSearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search shippers by contact name, company name, branch name"
      />
      <NetworkSortMenu value={sortType} onChange={setSortType} />
      <Box
        sx={{
          maxHeight: "500px",
          overflowY: "scroll",
        }}
      >
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Contact</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Date Added</TableCell>
                <TableCell>Notifications</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedFollowed.length > 0
                ? filteredAndSortedFollowed.map((f) => (
                    <EstablishedNetworkTableRow
                      key={f.id}
                      followed={f}
                      onUnfollowShipperClicked={onUnfollowShipperClicked}
                    />
                  ))
                : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Stack>
  );
}
